import type { UserParameters } from "@/types";
import { ApiHttpError } from "@/utils/apiHttpError";

export const META_PUT_DEBOUNCE_MS = 700;
export const META_PUT_MIN_INTERVAL_MS = 1100;
export const META_PUT_MAX_RETRIES = 2;
export const META_PUT_RETRY_DELAY_MS = 1500;
// Stop auto-rescheduling after this many consecutive failed sends so a server
// that keeps returning 429/5xx is not hammered forever (a fresh enqueue/flush
// resets the counter and gives it another chance).
export const META_PUT_MAX_CONSECUTIVE_FAILURES = 5;
export const META_PUT_MAX_BACKOFF_MS = 30000;

export type MetaPutScheduler = {
  enqueue: (partial: Partial<UserParameters>) => void;
  flush: () => Promise<void>;
  reset: () => void;
};

export type MetaPutSchedulerOptions = {
  put: (
    partial: Partial<UserParameters>,
    opts?: { keepalive?: boolean },
  ) => Promise<void>;
  debounceMs?: number;
  minIntervalMs?: number;
  maxRetries?: number;
  retryDelayMs?: number;
  maxConsecutiveFailures?: number;
  maxBackoffMs?: number;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
};

export function stableStringifyPartial(
  partial: Partial<UserParameters>,
): string {
  const keys = Object.keys(partial).sort();
  const sorted: Record<string, unknown> = {};
  for (const key of keys) {
    sorted[key] = partial[key as keyof UserParameters];
  }
  return JSON.stringify(sorted);
}

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isRetryablePutError(err: unknown): boolean {
  if (err instanceof ApiHttpError) {
    return err.status === 429 || err.status >= 500;
  }
  return err instanceof TypeError;
}

export function createMetaPutScheduler(
  options: MetaPutSchedulerOptions,
): MetaPutScheduler {
  const debounceMs = options.debounceMs ?? META_PUT_DEBOUNCE_MS;
  const minIntervalMs = options.minIntervalMs ?? META_PUT_MIN_INTERVAL_MS;
  const maxRetries = options.maxRetries ?? META_PUT_MAX_RETRIES;
  const retryDelayMs = options.retryDelayMs ?? META_PUT_RETRY_DELAY_MS;
  const maxConsecutiveFailures =
    options.maxConsecutiveFailures ?? META_PUT_MAX_CONSECUTIVE_FAILURES;
  const maxBackoffMs = options.maxBackoffMs ?? META_PUT_MAX_BACKOFF_MS;
  const now = options.now ?? (() => Date.now());
  const sleep = options.sleep ?? defaultSleep;

  let pending: Partial<UserParameters> = {};
  let lastAcknowledgedRaw: string | null = null;
  let lastSuccessAt = 0;
  let consecutiveFailures = 0;
  let debounceTimer: ReturnType<typeof setTimeout> | undefined;
  let throttleTimer: ReturnType<typeof setTimeout> | undefined;
  let isSending = false;

  function hasPendingKeys(): boolean {
    return Object.keys(pending).length > 0;
  }

  function snapshotPending(): Partial<UserParameters> {
    return { ...pending };
  }

  function clearSentFromPending(sent: Partial<UserParameters>) {
    for (const key of Object.keys(sent) as (keyof UserParameters)[]) {
      const sentRaw = JSON.stringify(sent[key]);
      const pendingValue = pending[key];
      if (pendingValue === undefined) continue;
      if (JSON.stringify(pendingValue) === sentRaw) {
        delete pending[key];
      }
    }
  }

  function backoffDelay(): number {
    return Math.min(
      minIntervalMs * 2 ** (consecutiveFailures - 1),
      maxBackoffMs,
    );
  }

  function scheduleDebounce() {
    if (debounceTimer !== undefined) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounceTimer = undefined;
      void attemptSend();
    }, debounceMs);
  }

  function scheduleAttempt(delayMs: number) {
    if (throttleTimer !== undefined) clearTimeout(throttleTimer);
    throttleTimer = setTimeout(() => {
      throttleTimer = undefined;
      void attemptSend();
    }, delayMs);
  }

  async function putWithRetry(
    payload: Partial<UserParameters>,
    keepalive: boolean,
  ): Promise<void> {
    let attempt = 0;
    while (true) {
      try {
        if (keepalive) await options.put(payload, { keepalive: true });
        else await options.put(payload);
        return;
      } catch (err) {
        if (!isRetryablePutError(err) || attempt >= maxRetries) {
          throw err;
        }
        attempt += 1;
        await sleep(retryDelayMs);
      }
    }
  }

  async function attemptSend(opts?: {
    force?: boolean;
    keepalive?: boolean;
  }): Promise<void> {
    if (isSending) return;
    if (!hasPendingKeys()) return;

    const elapsed = now() - lastSuccessAt;
    if (!opts?.force && lastSuccessAt > 0 && elapsed < minIntervalMs) {
      scheduleAttempt(minIntervalMs - elapsed);
      return;
    }

    const payload = snapshotPending();
    const raw = stableStringifyPartial(payload);
    if (raw === lastAcknowledgedRaw) {
      clearSentFromPending(payload);
      if (hasPendingKeys()) void attemptSend(opts);
      return;
    }

    isSending = true;
    try {
      await putWithRetry(payload, opts?.keepalive === true);
      lastAcknowledgedRaw = raw;
      lastSuccessAt = now();
      consecutiveFailures = 0;
      clearSentFromPending(payload);
    } catch {
      // Background sync: rate limits and transient errors are retried silently,
      // up to maxConsecutiveFailures (see the reschedule below) to avoid storms.
      consecutiveFailures += 1;
    } finally {
      isSending = false;
    }

    if (hasPendingKeys() && !opts?.force) {
      if (consecutiveFailures === 0) {
        scheduleAttempt(minIntervalMs);
      } else if (consecutiveFailures < maxConsecutiveFailures) {
        scheduleAttempt(backoffDelay());
      }
      // Otherwise give up auto-retrying until the next enqueue()/flush().
    }
  }

  function enqueue(partial: Partial<UserParameters>) {
    Object.assign(pending, partial);
    // New user intent deserves a fresh attempt even if prior sends were failing.
    consecutiveFailures = 0;
    scheduleDebounce();
  }

  async function flush(): Promise<void> {
    if (debounceTimer !== undefined) {
      clearTimeout(debounceTimer);
      debounceTimer = undefined;
    }
    // A pending throttle/backoff timer would only fire after the page is gone;
    // flush force-sends now (bypassing the min-interval) with keepalive so the
    // request survives unload.
    if (throttleTimer !== undefined) {
      clearTimeout(throttleTimer);
      throttleTimer = undefined;
    }

    for (let pass = 0; pass < 4; pass++) {
      if (!hasPendingKeys() && !isSending) return;

      if (isSending) {
        await sleep(0);
        continue;
      }

      await attemptSend({ force: true, keepalive: true });
    }
  }

  function reset() {
    pending = {};
    lastAcknowledgedRaw = null;
    lastSuccessAt = 0;
    consecutiveFailures = 0;
    isSending = false;
    if (debounceTimer !== undefined) clearTimeout(debounceTimer);
    if (throttleTimer !== undefined) clearTimeout(throttleTimer);
    debounceTimer = undefined;
    throttleTimer = undefined;
  }

  return { enqueue, flush, reset };
}
